import React, { Fragment } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Label, HTMLSelect, Classes, Dialog, Button,
} from '@blueprintjs/core';
import cloneDeep from 'lodash.clonedeep';
import { RootState } from 'data/root.reducers';
import { Warrant, WARRANT_LEA_ANY } from 'data/warrant/warrant.types';
import { FormTemplate, FormTemplateSection, Template } from 'data/template/template.types';
import WarrantAdapter from 'data/warrant/warrant.adapter';
import { CaseModel, CaseOptions as CaseOptionsType, TargetOptions } from 'data/case/case.types';
import {
  VisibilityScope, adaptTemplate, adaptTemplateVisibilityOptions, adaptTemplateLeaField, adaptTemplateTargetOptions, adaptInterceptTypeOptions, setCaseOptions, setTargetOptionsReadOnly, adaptTemplateDivisionField,
} from 'routes/Warrant/utils/warrantTemplate.adapter';
import { RetrievingDataProgress } from 'components';
import { CONFIG_FORM_MODE } from 'shared/popup/config-form-popup';
import { updateHI2Interfaces, updateHI3Interfaces } from 'xcipio/devices/forms/devices-to-cases';
import { TEMPORARY_CASE_ID_PREFIX } from 'xcipio/devices/forms/case-modeler';
import { CasePanel } from 'xcipio/cases/forms/case-panel';
import * as globalSelectors from 'global/global-selectors';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as warrantSelectors from 'data/warrant/warrant.selectors';
import * as caseActions from 'xcipio/cases/case-actions';
import * as caseSelectors from 'data/case/case.selectors';
import * as targetSelectors from 'data/target/target.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as userSelectors from 'users/users-selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { Team } from 'data/team/team.types';
import { getAllTeams } from 'teams/teams-actions';
import { SYSTEM_ADMIN_PRIVILEGE } from 'teams/utils/cspAdminUtils';
import { DEPLOYMENT_TUNISIA, QUERY_LOC_FROM_AMF_ID } from 'xcipio/constants';
import * as targetDomainActions from 'data/targetDomain/targetDomain.actions';
import * as targetDomainSelectors from 'data/targetDomain/targetDomain.selectors';
import * as targetTemplateActions from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.actions';
import { createWarrantBasedOnTemplate } from 'routes/Warrant/utils/warrantTemplate.factory';
import i18n from 'i18n';
import { targetOptionsTemplate } from 'xcipio/cases/TargetOptions/TargetOptions.types';
import { TargetModel } from 'data/target/target.types';
import WarrantDetails from './warrant-details';
import Templates from '../../templates/warrant-template';
import {
  checkGivenTimeWithCurrentTime,
  areTimesEqual,
  checkTimeValidationInComputerTimezone,
} from '../../../shared/constants/checkTimeValidation';
import CaseOptions from '../../cases/options/case-options';
import CaseCreateOptions from '../../cases/options/case-options.json';
import TargetOptionsDialog from '../../cases/TargetOptions/TargetOptionsDialog';
import { ChangedField } from '../../../shared/form';
import ManageAttachments from '../../attachments/index';
import UserAudit from '../../../shared/userAudit';
import { trimJsonObj } from '../../../shared/dataUtils/json-utils';
import './warrant-create.scss';

const fieldsPassedToCases = ['startDateTime', 'stopDateTime', 'timeZone', 'dxOwner'];

export const getCaseOptionTemplateValue = (templates: any, selectedTemplateId: string) => {
  let result = templates.filter((template: any) => template.id === selectedTemplateId)?.[0];
  if (!result) {
    [result] = CaseCreateOptions;
  }
  const selectCaseOptionTemplate = JSON.parse((result.template));
  const fields:any = { };
  Object.keys(selectCaseOptionTemplate.fields).forEach((field: string) => {
    fields[field] = selectCaseOptionTemplate.fields[field].initial;
  });
  return fields;
};

export const setTargetOptionTemplate = (warrant: Warrant, templates: { template: string }[]): void => {

  const shouldUpdateOptions = templates.some((template) => {
    const templateObject = cloneDeep(JSON.parse(template.template));

    return templateObject.key === 'caseOptionsQueryLocationFromAmf';
  });

  if (!shouldUpdateOptions) {
    return;
  }

  const targetOptionsFields = (targetOptionsTemplate.main as FormTemplateSection).fields;

  if (!warrant.targetOptions) {
    warrant.targetOptions = {};
  }

  Object.keys(targetOptionsFields).forEach((field) => {
    if (warrant?.targetOptions?.[field] != null) {
      warrant.targetOptions[field] = targetOptionsFields[field].initial as string || '0';
    }
  });
};

const mapState = (state: RootState) => ({
  templates: templateSelectors.getRawTemplates(state),
  warrant: warrantSelectors.getWarrant(state),
  cases: caseSelectors.getCases(state),
  targetsById: targetSelectors.getTargetsById(state),
  selectedDF: globalSelectors.getSelectedDeliveryFunctionId(state),
  changeTracker: warrantSelectors.getChangeTrackerInstance(state),
  warrantConfigInstance: warrantSelectors.getWarrantConfigurationInstance(state),
  userAudit: warrantSelectors.getWarrantUserAudit(state),
  timezones: userSelectors.getTimezones(state),
  teams: userSelectors.getTeams(state),
  allTeams: (state.teams?.teams ?? []) as Team[],
  isMultitenantEnabled: supportedFeaturesSelectors.isMultitenantEnabled(state),
  isSystemAdmin: authenticationSelectors.getAuthenticationPrivileges(state).includes(SYSTEM_ADMIN_PRIVILEGE),
  divisions: userSelectors.getDivisions(state),
  contacts: contactSelectors.getContacts(state),
  cfGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
  cfs: collectionFunctionSelectors.getCollectionFunctionList(state),
  pristineCollectionFunctions: collectionFunctionSelectors.getCollectionFunctionPristineList(state),
  attachmentEnabled: discoverySelectors.isAttachmentEnabled(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  selectAllHI3Interfaces: discoverySelectors.shouldSelectAllHi3Interfaces(state),
  isWarrantDefaultExpireEnabled: discoverySelectors.isWarrantDefaultExpireEnabled(state),
  warrantDefaultExpireDays: discoverySelectors.getWarrantDefaultExpireDays(state),
  targetDefaultAFSelection: warrantSelectors.getTargetDefaultAFSelection(state),
  hasDeviceChanged: warrantSelectors.hasWarrantDeviceChanged(state),
  targetDomainList: targetDomainSelectors.getTargetDomainList(state),
  allowedTargetsToSetOptions: discoverySelectors.allowedTargetsToSetOptions(state),
  deployment: discoverySelectors.getDiscoveryDeployment(state),
  isNokamflcsimEnabled: supportedFeaturesSelectors.isNokamflcsimEnabled(state),
  isDisplayLocDataInInterceptDropDownEnabled: discoverySelectors.isDisplayLocDataInInterceptDropDownEnabled(state),
  isSinglepointprovisioningEnabled: discoverySelectors.isSinglepointprovisioningEnabled(state),
});

const mapDispatch = {
  updateWarrantState: warrantActions.updateWarrantState,
  updateCasesState: caseActions.updateCasesState,
  updateWarrantAuditState: warrantActions.updateWarrantAuditState,
  updateFormTemplateUsed: warrantActions.updateFormTemplateUsed,
  clearCasesState: caseActions.clearCasesState,
  fetchCollectionFunctionListByIds: collectionFunctionActions.fetchCollectionFunctionListByIds,
  fetchCollectionFunctionList: collectionFunctionActions.fetchCollectionFunctionList,
  getTargetDomainsList: targetDomainActions.getTargetDomainsList,
  updateWarrantCustomizationDomainConfig: warrantActions.updateWarrantCustomizationDomainConfig,
  getTargetDeliveryTemplateAccessFunctionList: targetTemplateActions.getTargetDeliveryTemplateAccessFunctionList,
  getAllTeams,
};

const connector = connect(mapState, mapDispatch, null, { forwardRef: true });

type PropsFromRedux = ConnectedProps<typeof connector>;

export type WarrantPanelProps = PropsFromRedux & {
  mode: string
  hasConfiguredDevices: () => boolean
  warrantId?: string | null
};

export type WarrantPanelState = {
  formTemplate: Template,
  warrant: any,
  originalWarrant: Warrant | null,
  shouldWarrantDetailUpdate: boolean,
  isManageAttachmentVisible: boolean,
  isCaseOptionsVisible: boolean,
  isTargetOptionsVisible: boolean,
  isLEAChangeConfirmOpen: boolean,
  isRetrievingCfsServices: boolean
  isRetrievingInterfaces: boolean
}

type AssignableWarrantFields = 'startDateTime' | 'stopDateTime' | 'timeZone' | 'caseType' | 'dxOwner' | 'caseOptions' | 'caseOptTemplateUsed' | 'targetOptions';
type AssignableCaseFields = 'startDateTime' | 'stopDateTime' | 'timeZone' | 'type' | 'dxOwner' | 'options' | 'templateUsed' | 'targetOptions';

const WARRANT_TO_CASE_FIELD_NAME: { [key: string]: AssignableCaseFields } = {
  startDateTime: 'startDateTime',
  stopDateTime: 'stopDateTime',
  timeZone: 'timeZone',
  caseType: 'type',
  dxOwner: 'dxOwner',
  caseOptions: 'options',
  caseOptTemplateUsed: 'templateUsed',
  targetOptions: 'targetOptions',
};

type UpdateExistingCaseType = {
  name: AssignableWarrantFields
  value: string | CaseOptionsType | TargetOptions
}[]

type LeaChangeData = {
  changedField?: ChangedField
  prevValue?: unknown
}

export class WarrantPanel extends React.Component<WarrantPanelProps, WarrantPanelState> {
  warrantDetailRef: WarrantDetails | null = null
  auditRef: any
  templates: Templates
  shouldWarrantUpdate = true;
  leaChangeData: LeaChangeData = {}

  constructor(props: WarrantPanelProps) {
    super(props);
    this.templates = new Templates(props.templates);

    const {
      warrantId,
      mode,
      isWarrantDefaultExpireEnabled,
      warrantDefaultExpireDays,
      changeTracker,
      updateFormTemplateUsed,
      deployment
    } = this.props;

    let { warrant } = this.props;

    let formTemplate = this.templates.getById(warrant?.templateUsed ?? '', Templates.CATEGORY_WARRANT_WIZARD);
    formTemplate = this.getFormTemplate(formTemplate);

    if (warrant == null) {
      warrant = createWarrantBasedOnTemplate(formTemplate.template);
    }

    if (warrant.templateUsed == null || warrant.templateUsed === '') {
      // Set the template used if it is not already set
      warrant.templateUsed = formTemplate.id;
    }

    updateFormTemplateUsed(formTemplate);

    warrant = WarrantAdapter.adaptWarrantToForm(
      warrant,
      mode,
      isWarrantDefaultExpireEnabled,
      warrantDefaultExpireDays,
      changeTracker,
      warrantId,
    );

    if (mode === CONFIG_FORM_MODE.Edit || mode === CONFIG_FORM_MODE.View) {
      this.configureForEditMode(warrant);
    }

    const originalWarrant = cloneDeep(warrant);

    this.state = {
      warrant,
      originalWarrant,
      formTemplate,
      shouldWarrantDetailUpdate: true,
      isCaseOptionsVisible: false,
      isTargetOptionsVisible: false,
      isManageAttachmentVisible: false,
      isLEAChangeConfirmOpen: false,
      isRetrievingCfsServices: false,
      isRetrievingInterfaces: false,
    };
  }

  async componentDidMount() {
    const {
      selectedDF,
      getTargetDomainsList,
      deployment,
      mode,
      getTargetDeliveryTemplateAccessFunctionList,
      isMultitenantEnabled,
      isSystemAdmin,
      allTeams,
      getAllTeams,
    } = this.props;

    const {
      formTemplate,
    } = this.state;

    if (getTargetDomainsList) {
      getTargetDomainsList(selectedDF);
    }

    if (getTargetDeliveryTemplateAccessFunctionList) {
      getTargetDeliveryTemplateAccessFunctionList(selectedDF);
    }

    // A system administrator can give a multitenant warrant to any team, so needs them all.
    if (isMultitenantEnabled && isSystemAdmin && allTeams.length === 0) {
      getAllTeams();
    }

    if (deployment === DEPLOYMENT_TUNISIA && mode === CONFIG_FORM_MODE.Create) {
      const preSelectedLea = ((formTemplate.template as FormTemplate)?.general as FormTemplateSection).fields?.lea?.initial ?? '';
      const leaOption = (
          ((formTemplate.template as FormTemplate)
            ?.general as FormTemplateSection).fields?.lea?.options ?? []
        ).find((o) => o.value === preSelectedLea);
      if (leaOption) {
        await this.warrantDetailsFieldUpdateHandler({
          name: 'lea',
          label: 'LEA',
          value: leaOption?.value,
          valueLabel: leaOption?.label
        });
      }

      const preSelectedCaseOptTemplate = ((formTemplate.template as FormTemplate)?.general as FormTemplateSection).fields?.caseOptTemplateUsed?.initial ?? '';
      const caseTemplateOption = (
        ((formTemplate.template as FormTemplate)
          ?.general as FormTemplateSection).fields?.caseOptTemplateUsed?.options ?? []
      ).find((o) => o.value === preSelectedCaseOptTemplate);
      if (caseTemplateOption) {
        await this.warrantDetailsFieldUpdateHandler({
          name: 'caseOptTemplateUsed',
          label: 'Case Options Template',
          value: caseTemplateOption.value,
          valueLabel: caseTemplateOption.label
        })
      }
    }
  }

  componentDidUpdate(prevProps: WarrantPanelProps): void {
    const {
      mode,
      warrant,
      hasDeviceChanged,
      targetDomainList,
      updateWarrantCustomizationDomainConfig,
    } = this.props;

    const {
      formTemplate,
    } = this.state;

    if (WarrantPanel.shouldUpdateWarrantForm(mode, warrant, prevProps?.warrant)) {
      this.updateWarrantInformationForm();

    } else if (WarrantPanel.shouldAdaptTemplateLeaField(mode, hasDeviceChanged, prevProps?.hasDeviceChanged)) {
      const adaptedFormTemplate = adaptTemplateLeaField(formTemplate, hasDeviceChanged);
      this.setState({
        formTemplate: adaptedFormTemplate,
        shouldWarrantDetailUpdate: true,
      });
    }

    if (prevProps.teams !== this.props.teams
      || prevProps.allTeams !== this.props.allTeams
      || prevProps.isMultitenantEnabled !== this.props.isMultitenantEnabled
      || prevProps.isSystemAdmin !== this.props.isSystemAdmin) {
      const adaptedFormTemplate = adaptTemplateVisibilityOptions(
        formTemplate, mode, this.state.warrant, this.props.teams, this.getVisibilityScope(),
      );
      WarrantPanel.updateKey(adaptedFormTemplate);
      this.setState({
        formTemplate: adaptedFormTemplate,
        shouldWarrantDetailUpdate: true,
      });
    }

    if (targetDomainList?.length !== prevProps?.targetDomainList?.length) {
      updateWarrantCustomizationDomainConfig(targetDomainList[0]?.domain);
    }
  }

  updateWarrantInformationForm = (): void => {
    const {
      warrantId,
      mode,
      isWarrantDefaultExpireEnabled,
      warrantDefaultExpireDays,
      changeTracker,
    } = this.props;

    let { warrant } = this.props;

    const {
      formTemplate,
    } = this.state;

    warrant = WarrantAdapter.adaptWarrantToForm(
      warrant,
      mode,
      isWarrantDefaultExpireEnabled,
      warrantDefaultExpireDays,
      changeTracker,
      warrantId,
    );

    this.configureForEditMode(warrant);

    WarrantPanel.updateKey({ ...formTemplate });

    this.setState({
      shouldWarrantDetailUpdate: true,
      formTemplate,
      warrant,
    });
  }

  static shouldUpdateWarrantForm(
    mode:string,
    newWarrantState:Warrant | null,
    currentWarrantState:Warrant | null,
  ):boolean {
    if (!newWarrantState || !currentWarrantState) {
      return false;
    }

    const isViewMode = mode === CONFIG_FORM_MODE.View;

    const hasStatusChanged = newWarrantState?.state !== currentWarrantState?.state;
    const hasScheduleChanged =
      (newWarrantState?.startDateTime !== currentWarrantState?.startDateTime) ||
      (newWarrantState?.stopDateTime !== currentWarrantState?.stopDateTime);

    return isViewMode && (hasStatusChanged || hasScheduleChanged);
  }

  static shouldAdaptTemplateLeaField(
    mode: string,
    hasDeviceChanged: boolean,
    prevHasDeviceChanged: boolean,
  ): boolean {
    if (mode !== CONFIG_FORM_MODE.Edit) {
      return false;
    }

    return hasDeviceChanged !== prevHasDeviceChanged;
  }

  getVisibilityScope(): VisibilityScope {
    const { isMultitenantEnabled, isSystemAdmin, allTeams } = this.props;

    return { isMultitenantEnabled, isSystemAdmin, allTeams };
  }

  getFormTemplate(formTemplate: Template): Template {
    const {
      warrant,
      cases,
      mode,
      templates,
      attachmentEnabled,
      timezones,
      teams,
      divisions,
      contacts,
      pristineCollectionFunctions,
      cfGroups,
      hasDeviceChanged,
      deployment,
      isNokamflcsimEnabled,
      isDisplayLocDataInInterceptDropDownEnabled,
    } = this.props;

    const casesCollectionFunctionIds = cases?.map(({ cfId }) => cfId.id) ?? [];

    let adaptedFormTemplate = adaptTemplate(
      formTemplate,
      mode,
      warrant,
      templates,
      attachmentEnabled ?? false,
      timezones,
      contacts,
      teams,
      divisions,
      deployment,
      cfGroups,
      pristineCollectionFunctions,
      casesCollectionFunctionIds,
      this.handleManageAttachmentClick,
      this.showCaseOptions,
      this.showTargetOptions,
      this.getVisibilityScope(),
    );

    if (mode === CONFIG_FORM_MODE.Edit && hasDeviceChanged) {
      adaptedFormTemplate = adaptTemplateLeaField(adaptedFormTemplate, hasDeviceChanged);
    }

    adaptedFormTemplate = adaptTemplateTargetOptions(adaptedFormTemplate, templates, mode);
    adaptedFormTemplate = adaptInterceptTypeOptions(adaptedFormTemplate, isNokamflcsimEnabled, isDisplayLocDataInInterceptDropDownEnabled);
    adaptedFormTemplate = adaptTemplateDivisionField(adaptedFormTemplate, this.props.isSinglepointprovisioningEnabled);

    return adaptedFormTemplate;
  }

  configureForEditMode(warrant: Warrant): void {
    if (warrant.lea != null) {
      this.fetchCollectionFunctionsAndComputeServices(warrant.lea);
    }

    this.updateWarrantState(warrant);
  }

  isValid(errors: string[]): boolean {
    if (!errors) {
      errors = [];
    }

    const { warrant } = this.state;

    this.validateInterfieldRules(errors, warrant);

    const isValid = this.warrantDetailRef?.isValid() ?? false;

    if (this.auditRef) {
      const isAuditValid = this.auditRef.isValid();
      return (errors.length === 0 && isValid && isAuditValid);
    }

    return (errors.length === 0 && isValid);
  }

  isWarrantAndCasesValid(errors: string[]): [boolean, number | null] {
    const {
      cases,
      warrantConfigInstance,
    } = this.props;

    const {
      warrant,
      formTemplate,
    } = this.state;

    if (warrant == null || warrantConfigInstance == null) {
      return [false, null];
    }

    const { targetDefaultAFSelection } = this.props;
    const [areAllCasesValid, caseIndex] = CasePanel.areAllValid(
      warrant,
      cases,
      formTemplate.template.case as unknown as FormTemplate,
      warrantConfigInstance,
      errors,
      targetDefaultAFSelection,
    );

    return [areAllCasesValid, caseIndex];
  }

  validateInterfieldRules(errors: string[], formData: Warrant): void {
    const { mode } = this.props;
    const { originalWarrant } = this.state;

    const warrantStartDateTimeInDate = (!formData.startDateTime || formData.startDateTime === '') ? new Date() : new Date(formData.startDateTime);
    const warrantStopDateTimeInDate = new Date(formData.stopDateTime);

    if (mode === CONFIG_FORM_MODE.Create) {
      if (checkGivenTimeWithCurrentTime(formData.startDateTime, formData.timeZone)) {
        errors.push(i18n.t('warrant.form.fieldError.startDateTimeAfterCurrentTime'));
        return;
      }

      if (checkGivenTimeWithCurrentTime(formData.stopDateTime, formData.timeZone)) {
        errors.push(i18n.t('warrant.form.fieldError.stopDateTimeAfterCurrentTime'));
        return;
      }

      if (warrantStartDateTimeInDate.getTime() > warrantStopDateTimeInDate.getTime()) {
        errors.push(i18n.t('warrant.form.fieldError.stopDateTimeAfterStartTime'));
      }

    } else if (mode === CONFIG_FORM_MODE.Edit) {
      const hasAnyTimeChange = !areTimesEqual(
        originalWarrant?.startDateTime,
        originalWarrant?.stopDateTime,
        originalWarrant?.timeZone,
        formData.startDateTime,
        formData.stopDateTime,
        formData.timeZone,
      );

      if (hasAnyTimeChange) {
        const timeValidation = checkTimeValidationInComputerTimezone(
          originalWarrant?.startDateTime,
          originalWarrant?.stopDateTime as string,
        );

        if (!timeValidation && checkGivenTimeWithCurrentTime(formData.startDateTime, formData.timeZone)) {
          errors.push(i18n.t('warrant.form.fieldError.startDateTimeAndStopAfterCurrentTime'));
          return;
        }

        if (checkGivenTimeWithCurrentTime(formData.stopDateTime, formData.timeZone)) {
          errors.push(i18n.t('warrant.form.fieldError.stopDateTimeAfterCurrentTime'));
          return;
        }

        if (warrantStartDateTimeInDate.getTime() > warrantStopDateTimeInDate.getTime()) {
          errors.push(i18n.t('warrant.form.fieldError.stopDateTimeAfterStartTime'));
        }
      }
    }
  }

  static shouldUpdateExistingCases(fieldName: string): boolean {
    return !!WARRANT_TO_CASE_FIELD_NAME[fieldName];
  }

  async updateExistingCases(fields: UpdateExistingCaseType): Promise<void> {
    const {
      cases,
      changeTracker,
      targetsById,
      updateCasesState,
    } = this.props;

    const clonedCases = cloneDeep(cases);

    const updatingCasePromises: Promise<void>[] = [];
    clonedCases
      .filter((entry) => entry.id != null && !entry.id.startsWith(TEMPORARY_CASE_ID_PREFIX))
      .forEach((entry) => {

        fields.forEach(({ name, value }) => {

          const caseFieldName = WARRANT_TO_CASE_FIELD_NAME[name];
          const oldFieldValue = entry[caseFieldName];
          if (caseFieldName === 'options') {
            entry[caseFieldName] = value as CaseOptionsType;
          } else if (caseFieldName !== 'targetOptions') {
            entry[caseFieldName] = value as string;
          }

          if (caseFieldName === 'type') {
            const updateCasePromise = this.updateExistingCasesType(value as string, entry);
            updatingCasePromises.push(updateCasePromise);
          }

          if (caseFieldName === 'targetOptions') {

            entry.targets = entry?.targets.map((target) => {
              if (!target?.id) {
                return target;
              }

              if (target?.id && targetsById?.[target?.id]?.caseId?.id === entry.id) {
                const clonedTarget = cloneDeep(targetsById?.[target?.id]) as TargetModel;

                clonedTarget.options = (clonedTarget.options || []).map((option) => {
                  if (option.key !== 'LOC_POLL_FREQ') {
                    return option;
                  }

                  return {
                    key: 'LOC_POLL_FREQ',
                    value: (value as TargetOptions).LOC_POLL_FREQ as string,
                  };
                });

                return {
                  ...clonedTarget,
                  ...target,
                };
              }

              return targetsById?.[target?.id];
            });

            entry?.targets.forEach((target) => {
              if (
                target?.id != null &&
                changeTracker != null &&
                target?.options?.find((opt) => opt.key === 'LOC_POLL_FREQ') != null &&
                targetsById?.[target?.id]?.caseId?.id === entry.id
              ) {
                changeTracker
                  .addUpdated(
                    `case.${targetsById?.[target?.id]?.caseId?.id}.target.${targetsById[target.id].id}`,
                    value,
                    'options',
                    targetsById[target.id]?.options?.find((opt) => opt.key === 'LOC_POLL_FREQ')?.value,
                  );
              }
            });
          }

          if (changeTracker != null) {
            changeTracker.addUpdated(`case.${entry.id}`, value, caseFieldName, oldFieldValue);
          }
        });
      });

    await Promise.all(updatingCasePromises);

    updateCasesState(clonedCases);
  }

  async updateExistingCasesType(type: string, warrantCase: CaseModel): Promise<void> {
    const {
      changeTracker,
      selectAllHI3Interfaces,
      mode,
      selectedDF,
    } = this.props;

    if (warrantCase.hi2Data == null || warrantCase.hi3Data == null) {
      warrantCase.hi2Data = await updateHI2Interfaces(warrantCase.cfId.id, selectedDF);
      warrantCase.hi3Data = await updateHI3Interfaces(warrantCase.cfId.id, selectedDF);
    }

    const hi3Interfaces = CasePanel.createHI3Interfaces(type, warrantCase, selectAllHI3Interfaces ?? false, mode);
    warrantCase.hi3Interfaces = hi3Interfaces;

    if (changeTracker) {
      changeTracker.addHI3Interface(warrantCase.id);
    }

    if (type !== 'CC' && type !== 'ALL') {
      CasePanel.clearTargetsCCCvaluesCCRType(warrantCase, changeTracker);
    }
  }

  showCaseOptions = () => {
    this.shouldWarrantUpdate = true;
    this.setState({ isCaseOptionsVisible: true });
  }

  handleCaseOptionsClose = () => {
    this.setState({ isCaseOptionsVisible: false });
  }

  showTargetOptions = () => {
    this.shouldWarrantUpdate = true;
    if (this.state?.warrant?.caseOptTemplateUsed === QUERY_LOC_FROM_AMF_ID) {
      this.setState({ isTargetOptionsVisible: true });
    }
  }

  handleTargetOptionsClose = () => {
    this.setState({ isTargetOptionsVisible: false });
  }

  static updateKey = (formTemplate: any) => {
    formTemplate.template.key = `Key_${new Date().getTime()}`;
  }

  handleCaseOptionsSubmit = (options: any, templateId: any) => {
    const {
      updateWarrantState,
    } = this.props;

    const {
      warrant,
      formTemplate,
    } = this.state;

    const clonedOptions = cloneDeep(options);
    clonedOptions.configureOptions = true;

    const clonedWarrant = cloneDeep(warrant);
    clonedWarrant.caseOptions = clonedOptions;
    clonedWarrant.caseOptTemplateUsed = templateId;

    WarrantPanel.updateKey({ ...formTemplate });

    this.setState({
      warrant: clonedWarrant,
      isCaseOptionsVisible: false,
      isTargetOptionsVisible: false,
      shouldWarrantDetailUpdate: true,
      formTemplate,
    });

    if (WarrantPanel.shouldUpdateExistingCases('caseOptions') || WarrantPanel.shouldUpdateExistingCases('caseOptTemplateUsed')) {
      this.updateExistingCases([
        { name: 'caseOptions', value: clonedOptions },
        { name: 'caseOptTemplateUsed', value: templateId },
      ]);
    }

    updateWarrantState(clonedWarrant);
  }

  handleTargetOptionsSubmit = (targetOptions: TargetOptions): void => {
    const { updateWarrantState } = this.props;

    const {
      warrant,
      formTemplate,
    } = this.state;

    const clonedWarrant = cloneDeep(warrant);
    clonedWarrant.targetOptions = targetOptions;

    this.setState({
      warrant: clonedWarrant,
      isCaseOptionsVisible: false,
      isTargetOptionsVisible: false,
      shouldWarrantDetailUpdate: true,
      formTemplate,
    });

    if (WarrantPanel.shouldUpdateExistingCases('targetOptions')) {
      this.updateExistingCases([
        { name: 'targetOptions', value: targetOptions },
      ]);
    }

    updateWarrantState(clonedWarrant);
  }

  handleTemplateChange = (elem: any) => {
    const prevTemplateId = this.state.formTemplate.id;
    const prevTemplate = this.templates.getById(prevTemplateId, Templates.CATEGORY_WARRANT_WIZARD);

    const selectedTemplateId = elem.currentTarget.value;
    const selectedTemplate = this.templates.getById(selectedTemplateId, Templates.CATEGORY_WARRANT_WIZARD);
    const formTemplate = this.getFormTemplate(selectedTemplate);

    const warrant = cloneDeep(this.state.warrant);

    this.setWarrantFromTemplate(warrant, formTemplate);
    warrant.templateUsed = selectedTemplateId;

    this.setState({
      formTemplate,
      warrant,
      shouldWarrantDetailUpdate: true,
    });
    this.props.updateFormTemplateUsed(formTemplate);
    this.shouldWarrantUpdate = true;
    // when warrant template update, fire changeTracker warrant
    if (this.props.changeTracker) {
      // ChangeTracker takes the original value from the first time addUpdated is called on a field.
      // Subsequent invocation of addUpdated on the same field will not change the original value.
      // Therefore, we can keep passing in the new prevTemplateId even after we have provided
      // the original value in the first invocation.
      this.props.changeTracker.addUpdated(`warrant.${this.props.warrantId}.template`, selectedTemplate.name, i18n.t('warrant.changeTracker.template'), prevTemplate.name);
    }
  }

  setWarrantFromTemplate = (warrant: any, template: Template): void => {
    const { mode } = this.props;

    const { fields } = template.template.general as FormTemplateSection;
    Object.keys(fields).forEach((field) => {
      const { initial } = fields[field];
      // Dates were already converted to computer timezone when loaded
      if (initial !== undefined && !(mode === 'edit' && field === 'startDateTime' &&
        checkTimeValidationInComputerTimezone(warrant.startDateTime, warrant.stopDateTime))) {
        warrant[field] = initial;
      }
    });
  }

  warrantDetailsFieldUpdateHandler = async (changedField: ChangedField): Promise<void> => {
    const {
      name, value, label, valueLabel,
    } = changedField;
    // Edit warrants: Update Warrant
    const warrant = cloneDeep(this.state.warrant);
    const prevValue = warrant[name];
    const prevDisplayValue = prevValue; // todo: map prevValue to prevDisplayValue.

    this.shouldWarrantUpdate = false;

    if (name === 'lea') {
      this.setLeaChangeData({ changedField, prevValue });

      if (this.hasToConfirmLeaChange(prevValue, value)) {
        this.confirmLeaChange();
        return;
      }
      await this.leaChangeHandler(value);
    }

    warrant[name] = value;
    let fieldPassedToCases = '';
    if (fieldsPassedToCases.includes(name)) {
      fieldPassedToCases = name;
    }
    if (name === 'startDateTime' || name === 'stopDateTime' || name === 'receivedDateTime') {
      warrant[name] = trimJsonObj(value);
    }
    if (name === 'contact') {
      if (value === 'undefined') {
        warrant[name] = null;
      } else {
        warrant[name] = {
          id: value,
          name: value,
        };
      }
    }

    if (name === 'caseOptTemplateUsed') {
      warrant.caseOptions = getCaseOptionTemplateValue(this.props.templates, value);

      setTargetOptionTemplate(warrant, this.props.templates);

      const {
        formTemplate,
      } = this.state;
      const clonedFormTemplate = cloneDeep(formTemplate);
      setTargetOptionsReadOnly(clonedFormTemplate.template, value);

      this.setState({
        formTemplate: clonedFormTemplate,
        shouldWarrantDetailUpdate: true,
      });
      this.shouldWarrantUpdate = true;
    }

    if (name === 'caseType') {
      const {
        formTemplate,
      } = this.state;

      const {
        templates,
      } = this.props;
      const clonedFormTemplate = cloneDeep(formTemplate);

      if (value === 'LOC_ONLY_IN_IRI' || value === 'QUERY_LOC_FROM_AMF') {
        warrant.caseOptions = getCaseOptionTemplateValue(templates, '');
        setCaseOptions(clonedFormTemplate.template, templates, warrant?.caseType);
      } else {
        if (prevValue === 'LOC_ONLY_IN_IRI' || prevValue === 'QUERY_LOC_FROM_AMF') {
          warrant.caseOptTemplateUsed = '';
          warrant.caseOptions = getCaseOptionTemplateValue(templates, '');
          setTargetOptionsReadOnly(clonedFormTemplate.template, value);
        }
        setCaseOptions(clonedFormTemplate.template, templates, warrant?.caseType);
      }

      WarrantPanel.updateKey(clonedFormTemplate);

      this.setState({
        formTemplate: clonedFormTemplate,
        shouldWarrantDetailUpdate: true,
        warrant,
      });

      this.shouldWarrantUpdate = true;

    } else {

      const newState: any = {
        warrant,
      };

      if (fieldPassedToCases) {
        newState[fieldPassedToCases] = value;
        this.shouldWarrantUpdate = true;
      }

      this.setState(newState);

    }

    // when WarrantDetails fields update, fire changeTracker warrant
    if (this.props.changeTracker) {
      // ChangeTracker takes the original value from the first time addUpdated is called on a field.
      // Subsequent invocation of addUpdated on the same field will not change the original value.
      // Therefore, we can keep passing in the new prevValue even after we have provided
      // the original value in the first invocation.
      this.props.changeTracker.addUpdated(`warrant.${this.props.warrantId}.${name}`, valueLabel, label, prevDisplayValue);
      if (name === 'caseType' && (value === 'LOC_ONLY_IN_IRI' || value === 'QUERY_LOC_FROM_AMF')) {
        this.props.changeTracker.addUpdated(
          `warrant.${this.props.warrantId}.caseOptTemplateUsed`,
          i18n.t('warrant.case.form.fieldOption.type.locationDataOnly'),
          i18n.t('warrant.case.form.field.templateUsed'),
          prevDisplayValue,
        );
      }
    }

    if (WarrantPanel.shouldUpdateExistingCases(name)) {
      if (name === 'caseOptTemplateUsed') {
        const clonedOptions = cloneDeep(warrant.caseOptions);
        const targetOptions = cloneDeep(warrant.targetOptions);

        if (targetOptions) {
          this.updateExistingCases([
            { name: 'caseOptions', value: clonedOptions },
            { name: 'caseOptTemplateUsed', value },
            { name: 'targetOptions', value: targetOptions },
          ]);
        } else {
          this.updateExistingCases([
            { name: 'caseOptions', value: clonedOptions },
            { name: 'caseOptTemplateUsed', value },
          ]);
        }

      } else {
        this.updateExistingCases([{ name: name as AssignableWarrantFields, value }]);
      }
    }

    this.updateWarrantState(warrant);
  }

  handleManageAttachmentClick = (): void => {
    this.shouldWarrantUpdate = true;
    this.setState({ isManageAttachmentVisible: true });
  }

  handleManageAttachmentClose = (): void => {
    this.setState({ isManageAttachmentVisible: false });
  }

  handleManageAttachmentSubmit = (attachments: any): void => {
    const warrant = cloneDeep(this.state.warrant);
    warrant.attachments = attachments;
    const formTemplate = { ...this.state.formTemplate };
    (formTemplate.template.general as FormTemplateSection).fields.attachmentsDetails.initial = `${attachments.length} Attachments`;
    this.setState({
      warrant,
      formTemplate,
      isManageAttachmentVisible: false,
      shouldWarrantDetailUpdate: true,
    });
  }

  submit = async (): Promise<boolean> => {
    const {
      warrant,
    } = this.state;

    const clonedWarrant = cloneDeep(warrant);

    this.updateWarrantState(clonedWarrant);

    return true;
  }

  updateWarrantState(warrant: Warrant): void {
    const {
      isAuditEnabled,
      updateWarrantAuditState,
      updateWarrantState,
    } = this.props;

    if (isAuditEnabled && this.auditRef) { // update reducer
      const userAudit = this.auditRef.getValue();
      updateWarrantAuditState({ warrant, userAudit });

    } else {
      updateWarrantState(warrant);
    }
  }

  async fetchCollectionFunctionsAndComputeServices(lea: string): Promise<void> {
    await this.fetchCollectionFunctions(lea);
    await this.computeEnabledServicesForDevicePanel();
  }

  async fetchCollectionFunctions(lea: string): Promise<void> {
    const {
      fetchCollectionFunctionList,
      selectedDF,
      cfGroups,
      fetchCollectionFunctionListByIds,
    } = this.props;

    if (lea === WARRANT_LEA_ANY) {
      await fetchCollectionFunctionList(selectedDF);

    } else {
      const cfg = cfGroups.find((cfGroup: any) => {
        return cfGroup.id === lea;
      });

      const ids = cfg?.cfs.map((obj: any) => {
        return obj.id;
      });

      if (ids) {
        await fetchCollectionFunctionListByIds(selectedDF, ids.join(','));
      }
    }
  }

  async computeEnabledServicesForDevicePanel(): Promise<void> {
    const { cfs, warrantConfigInstance } = this.props;
    const cfTypes = WarrantPanel.cfsToTypes(cfs);
    await warrantConfigInstance?.computeEnabledServicesForDevicePanel(cfTypes);
  }

  static cfsToTypes = (cfs: any[]) => {
    const types = new Set<string>();
    cfs.forEach((cf: any) => {
      types.add(cf.tag);
    });
    return [...types];
  }

  setLeaChangeData(leaChangeData: LeaChangeData): void {
    this.leaChangeData = leaChangeData;
  }

  getLeaChangeData(): LeaChangeData {
    return this.leaChangeData;
  }

  hasToConfirmLeaChange(previousLeaValue: string, nextLeaValue: string): boolean {
    const {
      hasConfiguredDevices,
    } = this.props;

    const leaChanged = previousLeaValue != null && previousLeaValue !== '' && previousLeaValue !== nextLeaValue;

    return hasConfiguredDevices() && leaChanged;
  }

  async leaChangeHandler(lea: string): Promise<void> {
    const {
      mode,
      clearCasesState,
    } = this.props;

    if (mode === CONFIG_FORM_MODE.Create) {
      clearCasesState();
    }

    this.shouldWarrantUpdate = true;
    this.setState({ isRetrievingCfsServices: true });
    await this.fetchCollectionFunctionsAndComputeServices(lea);
    this.setState({ isRetrievingCfsServices: false });

    if (mode === CONFIG_FORM_MODE.Edit) {
      await this.updateCasesCollectionFunctionGroup(lea);
    }
  }

  confirmLeaChange(): void {
    this.setState({
      isLEAChangeConfirmOpen: true,
    });

    this.shouldWarrantUpdate = true;
  }

  leaChangeCancelHandler = (): void => {
    const {
      formTemplate,
    } = this.state;

    const clonedFormTemplate = cloneDeep(formTemplate);
    WarrantPanel.updateKey(clonedFormTemplate);

    this.setState({
      formTemplate: clonedFormTemplate,
      isLEAChangeConfirmOpen: false,
      shouldWarrantDetailUpdate: true,
    });
    this.shouldWarrantUpdate = true;
  }

  leaChangeConfirmHandler = async (): Promise<void> => {
    const {
      changeTracker,
      warrantId,
    } = this.props;

    const {
      warrant,
    } = this.state;

    const {
      changedField: {
        label,
        valueLabel,
        value,
      } = {},
      prevValue,
    } = this.getLeaChangeData();

    const clonedWarrant = cloneDeep(warrant);
    clonedWarrant.lea = value;

    this.setState({
      warrant: clonedWarrant,
      isLEAChangeConfirmOpen: false,
    });
    this.shouldWarrantUpdate = true;

    await this.leaChangeHandler(value);

    if (changeTracker) {
      changeTracker.addUpdated(`warrant.${warrantId}.lea`, valueLabel, label, prevValue);
    }

    this.updateWarrantState(clonedWarrant);
  }

  async updateCasesCollectionFunctionGroup(selectedCollectionFunctionGroupId: string): Promise<void> {
    const {
      cases,
      warrantConfigInstance,
      cfGroups,
      cfs,
      pristineCollectionFunctions,
      changeTracker,
      updateCasesState,
    } = this.props;

    if (warrantConfigInstance == null) {
      return;
    }

    this.setState({
      isRetrievingInterfaces: true,
    });

    const updatingCasePromises: Promise<void>[] = [];

    cases.forEach((entry) => {
      const currentCollectionFunctionTag = pristineCollectionFunctions.find(({ id }) => id === entry.cfId.id)?.tag;

      const chosenCollectionFunction = cfs.find((cfEntry) => cfEntry?.tag === currentCollectionFunctionTag);
      if (chosenCollectionFunction == null) {
        console.error('Could not find a new collection function to set for case', entry.id, currentCollectionFunctionTag);
        return;
      }

      if (changeTracker) {
        changeTracker.addUpdated(`case.${entry.id}.cfId`, chosenCollectionFunction.id, 'CF', entry.cfId.id);
      }

      entry.cfId = {
        id: chosenCollectionFunction.id,
        name: chosenCollectionFunction.name,
        tag: chosenCollectionFunction.tag,
      };

      // Simulate a case type change to force reloading the hi2 and hi3 interfaces
      const updateCasePromise = this.updateExistingCasesType(entry.type as string, entry);
      updatingCasePromises.push(updateCasePromise);
    });

    await Promise.all(updatingCasePromises);

    this.setState({
      isRetrievingInterfaces: false,
    });
  }

  shouldComponentUpdate = (): boolean => {
    return this.shouldWarrantUpdate;
  }

  renderWarrantState(): JSX.Element | null {
    const { mode } = this.props;
    const { warrant } = this.state;

    if (mode !== CONFIG_FORM_MODE.Edit && mode !== CONFIG_FORM_MODE.View) {
      return null;
    }

    return (
      <div className="field">
        <Label className="bp3-inline">
          {i18n.t('warrant.form.state')} <span className="warrant-state">{warrant.state}</span>
        </Label>
      </div>
    );
  }

  render(): JSX.Element {
    const {
      formTemplate,
      warrant,
      shouldWarrantDetailUpdate,
      isManageAttachmentVisible,
      isCaseOptionsVisible,
      isTargetOptionsVisible,
      isLEAChangeConfirmOpen,
      isRetrievingCfsServices,
      isRetrievingInterfaces,
    } = this.state;

    const {
      mode,
      changeTracker,
      isAuditEnabled,
      userAudit,
      templates,
      warrantId,
      targetDomainList,
      allowedTargetsToSetOptions,
    } = this.props;

    return (
      <Fragment>
        <div className={mode === 'view' ? '' : 'formEnabled'}>
          <div className="form-flex-row-css">
            <div className="field">
              <Label className="bp3-inline">
                { /*
                  {i18n.t('warrant.form.template')}
                  <HTMLSelect
                    options={this.templates.getDropdownOptions(Templates.CATEGORY_WARRANT_WIZARD)}
                    onChange={this.handleTemplateChange}
                    value={formTemplate.id}
                    disabled={mode === CONFIG_FORM_MODE.View}
                  /> */
                }
              </Label>
            </div>
            {this.renderWarrantState()}
            <div className="field" />
            <div className="field" />
            <div className="field" />
          </div>
        </div>
        <WarrantDetails
          ref={(ref) => { this.warrantDetailRef = ref; }}
          formTemplate={formTemplate.template}
          defaults={warrant}
          shouldWarrantDetailUpdate={shouldWarrantDetailUpdate}
          fieldUpdateHandler={this.warrantDetailsFieldUpdateHandler}
          mode={mode}
        />
        {isAuditEnabled && (
          <UserAudit
            data-test="userAudit"
            ref={(ref: any) => { this.auditRef = ref; }}
            defaults={userAudit}
            mode={mode}
          />
        )}
        <ManageAttachments
          isOpen={isManageAttachmentVisible}
          warrantAttachments={warrant.attachments}
          handleClose={this.handleManageAttachmentClose}
          handleSubmit={this.handleManageAttachmentSubmit}
          mode={mode}
          changeTracker={changeTracker}
        />
        <CaseOptions
          isOpen={isCaseOptionsVisible}
          currentStateMode={mode}
          handleClose={this.handleCaseOptionsClose}
          inputCaseOptions={warrant.caseOptions ?? null}
          handleSubmit={this.handleCaseOptionsSubmit}
          caseId={undefined}
          warrantId={warrantId}
          templateUsed={warrant.caseOptTemplateUsed ?? null}
          templates={templates}
          interceptType={warrant.caseType}
          deployment={this.props.deployment}
        />
        <TargetOptionsDialog
          data={warrant?.targetOptions}
          title={i18n.t('warrant.target.titleTargetOptions')}
          message={i18n.t('warrant.target.message')}
          isOpen={isTargetOptionsVisible}
          shoudConfigPullLocationUpdates
          shoudConfigTargetDomainList={false}
          shouldConfigTargetOptions={false}
          targetDomainList={targetDomainList}
          onClose={this.handleTargetOptionsClose}
          onSubmit={this.handleTargetOptionsSubmit}
          currentStateMode={mode}
          allowedTargetsToSetOptions={allowedTargetsToSetOptions}
        />
        <Dialog
          autoFocus
          isOpen={isLEAChangeConfirmOpen}
          title={i18n.t('warrant.leaPopup.title')}
          className=""
          onClose={this.leaChangeCancelHandler}
        >
          <div className={Classes.DIALOG_BODY}>
            <p>
              {i18n.t('warrant.leaPopup.description')}
              <br /><br />
              {i18n.t('warrant.leaPopup.message')}
            </p>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button data-testid="ConfirmLEAChangedCancelButton" onClick={this.leaChangeCancelHandler}>{i18n.t('common.button.cancel')}</Button>
              <Button data-testid="ConfirmLEAChangedConfirmButton" intent="primary" onClick={this.leaChangeConfirmHandler}>{i18n.t('warrant.leaPopup.action')}</Button>
            </div>
          </div>
        </Dialog>
        <RetrievingDataProgress isOpen={isRetrievingCfsServices} message={i18n.t('warrant.edit.retrievingServices')} />
        <RetrievingDataProgress isOpen={isRetrievingInterfaces} message={i18n.t('warrant.case.form.retrievingInterfaces')} />
      </Fragment>
    );
  }
}

export default connector(WarrantPanel);
